import {
  BriefcaseIcon,
  CalendarDaysIcon,
  ChartColumnIcon,
  CircleCheckIcon,
  CopyIcon,
  FileTextIcon,
  FlagIcon,
  FolderKanbanIcon,
  GlobeIcon,
  LightbulbIcon,
  ListChecksIcon,
  MegaphoneIcon,
  NotebookPenIcon,
  PaletteIcon,
  RocketIcon,
  SquareKanbanIcon,
  StarIcon,
  StoreIcon,
  TargetIcon,
  UsersIcon,
  BugIcon,
  type LucideIcon,
} from 'lucide-react';
import type { TrackerHue, TrackerIcon } from '@inference.engine/workspace-api-client/web';

export type { TrackerHue, TrackerIcon };

export const TRACKER_HUES = [
  'neutral',
  'blue',
  'purple',
  'teal',
  'green',
  'yellow',
  'orange',
  'red',
] as const satisfies readonly TrackerHue[];

/** Picker order, which is also the order the design lays the grid out in. */
export const TRACKER_ICONS = [
  'lightbulb',
  'file-text',
  'notebook-pen',
  'folder-kanban',
  'circle-check',
  'calendar-days',
  'megaphone',
  'bug',
  'square-kanban',
  'copy',
  'store',
  'users',
  'chart-column',
  'rocket',
  'target',
  'flag',
  'star',
  'briefcase',
  'palette',
  'list-checks',
  'globe',
] as const satisfies readonly TrackerIcon[];

export const TRACKER_ICON_COMPONENTS: Record<TrackerIcon, LucideIcon> = {
  lightbulb: LightbulbIcon,
  'file-text': FileTextIcon,
  'notebook-pen': NotebookPenIcon,
  'folder-kanban': FolderKanbanIcon,
  'circle-check': CircleCheckIcon,
  'calendar-days': CalendarDaysIcon,
  megaphone: MegaphoneIcon,
  bug: BugIcon,
  'square-kanban': SquareKanbanIcon,
  copy: CopyIcon,
  store: StoreIcon,
  users: UsersIcon,
  'chart-column': ChartColumnIcon,
  rocket: RocketIcon,
  target: TargetIcon,
  flag: FlagIcon,
  star: StarIcon,
  briefcase: BriefcaseIcon,
  palette: PaletteIcon,
  'list-checks': ListChecksIcon,
  globe: GlobeIcon,
};

/**
 * The hue tokens do not change in dark mode, so each light pair carries a dark one. Classes are
 * spelled out in full because Tailwind only generates what it finds written in the source.
 */
export const TRACKER_HUE_CLASSES: Record<
  TrackerHue,
  { tile: string; foreground: string; pressed: string; border: string; edge: string }
> = {
  neutral: {
    tile: 'bg-secondary',
    foreground: 'text-foreground',
    pressed: 'data-[state=on]:bg-secondary data-[state=on]:text-foreground',
    border: 'border-foreground',
    edge: 'ring-foreground/20',
  },
  blue: {
    tile: 'bg-blue-100 dark:bg-blue-900',
    foreground: 'text-blue-800 dark:text-blue-300',
    pressed:
      'data-[state=on]:bg-blue-100 dark:data-[state=on]:bg-blue-900 data-[state=on]:text-blue-800 dark:data-[state=on]:text-blue-300',
    border: 'border-blue-800 dark:border-blue-300',
    edge: 'ring-blue-800/20 dark:ring-blue-300/20',
  },
  purple: {
    tile: 'bg-purple-100 dark:bg-purple-900',
    foreground: 'text-purple-800 dark:text-purple-300',
    pressed:
      'data-[state=on]:bg-purple-100 dark:data-[state=on]:bg-purple-900 data-[state=on]:text-purple-800 dark:data-[state=on]:text-purple-300',
    border: 'border-purple-800 dark:border-purple-300',
    edge: 'ring-purple-800/20 dark:ring-purple-300/20',
  },
  teal: {
    tile: 'bg-teal-100 dark:bg-teal-900',
    foreground: 'text-teal-800 dark:text-teal-300',
    pressed:
      'data-[state=on]:bg-teal-100 dark:data-[state=on]:bg-teal-900 data-[state=on]:text-teal-800 dark:data-[state=on]:text-teal-300',
    border: 'border-teal-800 dark:border-teal-300',
    edge: 'ring-teal-800/20 dark:ring-teal-300/20',
  },
  green: {
    tile: 'bg-green-100 dark:bg-green-900',
    foreground: 'text-green-900 dark:text-green-300',
    pressed:
      'data-[state=on]:bg-green-100 dark:data-[state=on]:bg-green-900 data-[state=on]:text-green-900 dark:data-[state=on]:text-green-300',
    border: 'border-green-900 dark:border-green-300',
    edge: 'ring-green-900/20 dark:ring-green-300/20',
  },
  yellow: {
    tile: 'bg-yellow-100 dark:bg-yellow-900',
    foreground: 'text-yellow-800 dark:text-yellow-300',
    pressed:
      'data-[state=on]:bg-yellow-100 dark:data-[state=on]:bg-yellow-900 data-[state=on]:text-yellow-800 dark:data-[state=on]:text-yellow-300',
    border: 'border-yellow-800 dark:border-yellow-300',
    edge: 'ring-yellow-800/20 dark:ring-yellow-300/20',
  },
  orange: {
    tile: 'bg-orange-100 dark:bg-orange-900',
    foreground: 'text-orange-800 dark:text-orange-300',
    pressed:
      'data-[state=on]:bg-orange-100 dark:data-[state=on]:bg-orange-900 data-[state=on]:text-orange-800 dark:data-[state=on]:text-orange-300',
    border: 'border-orange-800 dark:border-orange-300',
    edge: 'ring-orange-800/20 dark:ring-orange-300/20',
  },
  red: {
    tile: 'bg-red-100 dark:bg-red-900',
    foreground: 'text-red-800 dark:text-red-300',
    pressed:
      'data-[state=on]:bg-red-100 dark:data-[state=on]:bg-red-900 data-[state=on]:text-red-800 dark:data-[state=on]:text-red-300',
    border: 'border-red-800 dark:border-red-300',
    edge: 'ring-red-800/20 dark:ring-red-300/20',
  },
};

export function isTrackerIcon(value: string | undefined): value is TrackerIcon {
  return value !== undefined && Object.hasOwn(TRACKER_ICON_COMPONENTS, value);
}

export function isTrackerHue(value: string | undefined): value is TrackerHue {
  return value !== undefined && Object.hasOwn(TRACKER_HUE_CLASSES, value);
}

/** Stored values can outlive the client's list, so an unknown icon or hue renders the defaults. */
export function trackerIconComponent(icon: string | undefined): LucideIcon {
  return isTrackerIcon(icon) ? TRACKER_ICON_COMPONENTS[icon] : ListChecksIcon;
}

export function trackerHueClasses(hue: string | undefined) {
  return isTrackerHue(hue) ? TRACKER_HUE_CLASSES[hue] : TRACKER_HUE_CLASSES.neutral;
}

export const TRACKER_HUE_LABEL_KEYS = {
  neutral: 'trackers.trackerAppearance.hues.neutral',
  blue: 'trackers.trackerAppearance.hues.blue',
  purple: 'trackers.trackerAppearance.hues.purple',
  teal: 'trackers.trackerAppearance.hues.teal',
  green: 'trackers.trackerAppearance.hues.green',
  yellow: 'trackers.trackerAppearance.hues.yellow',
  orange: 'trackers.trackerAppearance.hues.orange',
  red: 'trackers.trackerAppearance.hues.red',
} as const satisfies Record<TrackerHue, string>;

export const TRACKER_ICON_LABEL_KEYS = {
  lightbulb: 'trackers.trackerAppearance.icons.lightbulb',
  'file-text': 'trackers.trackerAppearance.icons.document',
  'notebook-pen': 'trackers.trackerAppearance.icons.notebook',
  'folder-kanban': 'trackers.trackerAppearance.icons.projectFolder',
  'circle-check': 'trackers.trackerAppearance.icons.checkmark',
  'calendar-days': 'trackers.trackerAppearance.icons.calendar',
  megaphone: 'trackers.trackerAppearance.icons.megaphone',
  bug: 'trackers.trackerAppearance.icons.bug',
  'square-kanban': 'trackers.trackerAppearance.icons.kanbanBoard',
  copy: 'trackers.trackerAppearance.icons.copies',
  store: 'trackers.trackerAppearance.icons.store',
  users: 'trackers.trackerAppearance.icons.people',
  'chart-column': 'trackers.trackerAppearance.icons.barChart',
  rocket: 'trackers.trackerAppearance.icons.rocket',
  target: 'trackers.trackerAppearance.icons.target',
  flag: 'trackers.trackerAppearance.icons.flag',
  star: 'trackers.trackerAppearance.icons.star',
  briefcase: 'trackers.trackerAppearance.icons.briefcase',
  palette: 'trackers.trackerAppearance.icons.palette',
  'list-checks': 'trackers.trackerAppearance.icons.checklist',
  globe: 'trackers.trackerAppearance.icons.globe',
} as const satisfies Record<TrackerIcon, string>;
